import React from 'react';
import { NavLink, Link, useNavigate } from 'react-router-dom';
import { Home, Video, Image as ImageIcon, FileBox as FileGifBox, Music, FileText, LogOut, Settings, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';

const navItems = [
  { to: "/dashboard", icon: Home, label: "Tudo" },
  { to: "/studio", icon: Sparkles, label: "Studio Integrativo" },
  { to: "/videos", icon: Video, label: "Vídeos" },
  { to: "/images", icon: ImageIcon, label: "Imagens" },
  { to: "/gifs", icon: FileGifBox, label: "GIFs" },
  { to: "/audios", icon: Music, label: "Áudios" },
  { to: "/documents", icon: FileText, label: "Documentos" },
];

const Sidebar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
      toast({
        title: "Logout realizado",
        description: "Você saiu da sua conta com sucesso.",
      });
    } catch (error) {
      console.error("Erro ao sair:", error);
      toast({
        title: "Erro ao sair",
        description: "Não foi possível realizar o logout.",
        variant: "destructive",
      });
    }
  };

  const userInitials = user?.email 
    ? user.email.substring(0, 2).toUpperCase() 
    : 'U';

  const userName = user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'Usuário';

  return (
    <aside className="h-full flex flex-col bg-slate-900/50 backdrop-blur-xl border-r border-slate-800/50">
      <div className="flex items-center gap-3 p-6 mb-2">
        <motion.div
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ duration: 0.5, type: 'spring', stiffness: 120 }}
          className="w-10 h-10 bg-gradient-to-br from-blue-600 to-cyan-400 rounded-xl flex items-center justify-center shadow-lg shadow-cyan-500/20"
        >
          <ImageIcon className="text-white h-6 w-6" />
        </motion.div>
        <span className="text-xl font-black bg-clip-text text-transparent bg-gradient-to-r from-white to-slate-400 tracking-tight">
          MediaHub
        </span>
      </div>

      <nav className="flex-1 px-4 space-y-2 overflow-y-auto custom-scrollbar">
        <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider px-4 mb-2 mt-4">
          Menu
        </div>
        {navItems.map((item, index) => (
          <motion.div
            key={item.to}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <NavLink
              to={item.to}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300 group relative overflow-hidden ${
                  isActive
                    ? 'bg-gradient-to-r from-blue-600/20 to-cyan-500/20 text-cyan-400 font-semibold shadow-inner shadow-cyan-500/10'
                    : 'text-slate-400 hover:text-slate-100 hover:bg-slate-800/50'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {isActive && (
                    <motion.div
                      layoutId="activeTab"
                      className="absolute left-0 w-1 h-6 bg-cyan-400 rounded-r-full"
                    />
                  )}
                  <item.icon className={`h-5 w-5 transition-colors duration-300 ${isActive ? 'text-cyan-400' : 'text-slate-500 group-hover:text-slate-300'}`} />
                  <span className="relative z-10">{item.label}</span>
                  {isActive && (
                    <motion.div
                      layoutId="glow"
                      className="absolute inset-0 bg-cyan-400/5 rounded-xl blur-xl"
                      transition={{ duration: 0.5 }}
                    />
                  )}
                </>
              )}
            </NavLink>
          </motion.div>
        ))}
      </nav>

      <div className="p-4 border-t border-slate-800/50">
        <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center text-sm font-bold text-white shadow-lg shadow-purple-500/20">
              {userInitials}
            </div>
            <div className="overflow-hidden">
              <div className="text-sm font-medium text-slate-200 truncate">{userName}</div>
              <div className="text-xs text-slate-500 truncate">{user?.email}</div>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-2">
            <Link to="/profile">
              <Button variant="outline" size="sm" className="w-full justify-start gap-2 bg-slate-900/50 border-slate-700 hover:bg-slate-800 text-slate-300 hover:text-white h-9 text-xs">
                <Settings className="w-3 h-3" />
                Perfil
              </Button>
            </Link>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleLogout}
              className="w-full justify-start gap-2 bg-slate-900/50 border-slate-700 hover:bg-red-900/20 hover:text-red-400 hover:border-red-900/50 text-slate-300 h-9 text-xs"
            >
              <LogOut className="w-3 h-3" />
              Sair
            </Button>
          </div>
        </div>
        <div className="mt-2 text-center text-[9px] leading-tight text-slate-700">
          MediaHub · desenvolvido por CylinderMargin Lda.
          <span className="mx-1">·</span>
          <Link to="/terms" className="hover:text-slate-500">Termos</Link>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
